/* @flow */
import _ from 'lodash';
import type { Action, ReduxState, Fetchable, Smartlist, SmartlistItem } from '../../types';

// ===========================
//   STATE TYPE
// ===========================
export type SmartlistState = {
  smartlist: Fetchable<Smartlist>,
  items: Fetchable<SmartlistItem[]>,
  pageScroll: number,
  selectedItems: {
    [itemId: number]: boolean,
  },
  expandedItems: {
    [itemId: number]: boolean,
  },
};

// ===========================
//   INITIAL STATE
// ===========================

const initialPageScroll = 0;
const initialSelectedItems = {};
const initialExpandedItems = {};

export const initialState: SmartlistState = {
  smartlist: { fetching: false },
  items: { fetching: false },
  pageScroll: initialPageScroll,
  selectedItems: initialSelectedItems,
  expandedItems: initialExpandedItems,
};

// ===========================
//   REDUCER
// ===========================
export default (state: SmartlistState = initialState, action: Action): SmartlistState => {
  switch (action.type) {
    case 'FETCH_SMARTLIST_REQUEST': {
      return {
        ...state,
        smartlist: { fetching: true },
      };
    }
    case 'FETCH_SMARTLIST_FAILURE': {
      const error = action.payload;
      return {
        ...state,
        smartlist: { fetching: false, error },
        pageScroll: initialPageScroll,
        selectedItems: initialSelectedItems,
        expandedItems: initialExpandedItems,
      };
    }
    case 'FETCH_SMARTLIST_SUCCESS': {
      const data = action.payload;
      return {
        ...state,
        smartlist: { fetching: false, data },
        pageScroll: initialPageScroll,
        selectedItems: initialSelectedItems,
        expandedItems: initialExpandedItems,
      };
    }
    case 'FETCH_SMARTLIST_ITEMS_REQUEST': {
      return {
        ...state,
        items: { fetching: true },
      };
    }
    case 'FETCH_SMARTLIST_ITEMS_FAILURE': {
      const error = action.payload;
      return {
        ...state,
        items: { fetching: false, error },
        pageScroll: initialPageScroll,
        selectedItems: initialSelectedItems,
        expandedItems: initialExpandedItems,
      };
    }
    case 'FETCH_SMARTLIST_ITEMS_SUCCESS': {
      const data = action.payload;
      return {
        ...state,
        items: { fetching: false, data },
        pageScroll: initialPageScroll,
        selectedItems: initialSelectedItems,
        expandedItems: initialExpandedItems,
      };
    }
    case 'SMARTLIST_SCROLL_PAGE': {
      const pageScroll = action.payload || 0;
      return {
        ...state,
        pageScroll,
      };
    }
    case 'SMARTLIST_EXPAND_ITEM': {
      const { itemId, isExpanded } = action.payload;
      const { expandedItems } = state;
      expandedItems[itemId] = isExpanded;
      return {
        ...state,
        expandedItems,
      };
    }
    case 'SMARTLIST_TOGGLE_SELECT_ITEMS': {
      const itemIds = action.payload;
      const { selectedItems } = state;
      const selectedItemIds = _.map(_.keys(_.pickBy(selectedItems, v => v === true)), v =>
        Number(v)
      );
      const isAllSelected = itemIds.every(id => selectedItemIds.includes(id));
      for (const itemId of itemIds) {
        selectedItems[itemId] = !isAllSelected;
      }
      return {
        ...state,
        selectedItems,
      };
    }
    case 'SMARTLIST_TOGGLE_EXPAND_SELECTED_ITEMS': {
      const { expandedItems, selectedItems } = state;
      const selectedItemIds = _.map(_.keys(_.pickBy(selectedItems, v => v === true)), v =>
        Number(v)
      );
      const expandedItemIds = _.map(_.keys(_.pickBy(expandedItems, v => v === true)), v =>
        Number(v)
      );
      const isAllExpanded = selectedItemIds.every(id => expandedItemIds.includes(id));
      for (const itemId of selectedItemIds) {
        expandedItems[itemId] = !isAllExpanded;
      }
      return {
        ...state,
        expandedItems,
      };
    }

    case 'LOGOUT_SUCCESS':
      return initialState;

    default: {
      return state;
    }
  }
};

// ===========================
//   SELECTORS
// ===========================

export const getSelectedItemIds = (state: ReduxState): number[] => {
  return _.map(_.keys(_.pickBy(state.smartlist.selectedItems, v => v === true)), v => Number(v));
};

export const getExpandedItemIds = (state: ReduxState): number[] => {
  return _.map(_.keys(_.pickBy(state.smartlist.expandedItems, v => v === true)), v => Number(v));
};
